import React from 'react';
import Button from '../../components/Form/Button';
import { FinanceReport } from '../../types';

interface ReportSummaryProps {
  report: FinanceReport;
  onExportCSV: () => void;
}

const ReportSummary: React.FC<ReportSummaryProps> = ({ report, onExportCSV }) => (
  <div className="bg-white shadow rounded-lg">
    <div className="px-4 py-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg leading-6 font-medium text-gray-900">{report.title}</h3>
        <Button onClick={onExportCSV} variant="secondary" size="sm">
          Export CSV
        </Button>
      </div>
      
      <p className="text-sm text-gray-600 mb-6">{report.description}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-green-800">Total Income</h4>
          <p className="text-lg font-semibold text-green-900">
            ${report.summary.totalIncome.usd.toLocaleString()}
          </p>
          <p className="text-sm text-green-700">
            {report.summary.totalIncome.egp.toLocaleString()} EGP
          </p>
        </div>

        <div className="bg-red-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-red-800">Total Expenses</h4>
          <p className="text-lg font-semibold text-red-900">
            ${report.summary.totalExpenses.usd.toLocaleString()}
          </p>
          <p className="text-sm text-red-700">
            {report.summary.totalExpenses.egp.toLocaleString()} EGP
          </p>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-blue-800">Net Profit</h4>
          <p className="text-lg font-semibold text-blue-900">
            ${report.summary.netProfit.usd.toLocaleString()}
          </p>
          <p className="text-sm text-blue-700">
            {report.summary.netProfit.egp.toLocaleString()} EGP
          </p>
        </div>

        {report.summary.conversionDifferences && (
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-yellow-800">Conversion Impact</h4>
            <p className="text-lg font-semibold text-yellow-900">
              {report.summary.conversionDifferences.net.toLocaleString()} EGP
            </p>
            <p className="text-xs text-yellow-700">
              Gain: {report.summary.conversionDifferences.gain.toLocaleString()} | 
              Loss: {report.summary.conversionDifferences.loss.toLocaleString()}
            </p>
          </div>
        )}
      </div>

      <div className="mb-6">
        <h4 className="text-md font-medium text-gray-900 mb-3">Breakdown by Module</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(report.summary.moduleBreakdown).map(([module, amounts]) => (
            <div key={module} className="bg-gray-50 p-3 rounded">
              <h5 className="text-sm font-medium text-gray-800">{module}</h5>
              <p className="text-sm text-gray-600">
                ${amounts.usd.toLocaleString()} | {amounts.egp.toLocaleString()} EGP
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default ReportSummary;
